import {
  Badge,
  Box,
  Button,
  Checkbox,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { KYDTicket, KYDTicketType } from "@common/interfaces/KYDTicket";
import { ErrorMessage } from "@hookform/error-message";
import { useCallback, useContext, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { AlgoliaAttendeeWithTickets } from "./AttendeesSection";
import { CreatableSelect } from "chakra-react-select";
import dayjs from "dayjs";
import actions from "../../../../actions";
import { useParams } from "react-router-dom";
import { getTransferredTicketKind } from "./transferredTicket";

export const EditAttendeeDialogContext = createDialogContext();

export type EditAttendeeDialogValues = {
  id?: string;
  objectID?: string;
  notes: string;
  tags: string[];
};

const EditAttendeeDialog = ({
  onSave,
  ticketTypes,
  timezone,
  isLoading = false,
  editingAttendee,
}: {
  isLoading: boolean;
  ticketTypes: KYDTicketType[];
  timezone: string;
  editingAttendee: AlgoliaAttendeeWithTickets;
  onSave: (data: EditAttendeeDialogValues) => void;
}) => {
  const bodyRef = useRef();
  const { isOpen, close } = useContext(EditAttendeeDialogContext);
  const [isResending, setIsResending] = useState<string | null>(null);
  const toast = useToast();
  const { event_id } = useParams();

  const resendTickets = useCallback(
    async (payment_id: string) => {
      setIsResending(payment_id);
      const response = await actions.ADMIN.resendReceipt(
        event_id,
        editingAttendee.id,
        payment_id
      )
        .then(() => {
          toast({
            title: "Receipt sent",
            description: `Receipt sent to ${editingAttendee.email}`,
            status: "success",
            duration: 5000,
            isClosable: true,
          });
        })
        .finally(() => {
          setIsResending(null);
        });
    },
    [editingAttendee]
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
    control,
  } = useForm<EditAttendeeDialogValues>({
    mode: "all",
    criteriaMode: "all",
    values: {
      id: editingAttendee?.id,
      notes: editingAttendee?.notes,
      tags: editingAttendee?.kyd_tags,
      objectID: editingAttendee?.objectID,
    },
    disabled: isLoading,
  });

  const _onSave = (data: EditAttendeeDialogValues) => {
    return onSave({
      id: data.id,
      notes: data.notes,
      tags: data.tags,
      objectID: data.objectID,
    });
  };

  const onClose = () => {
    close();
    reset();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">
                {editingAttendee?.first_name} {editingAttendee?.last_name}
              </Text>
              <Text w="full" fontSize="sm">
                {editingAttendee?.email || "No email"}
              </Text>
              {editingAttendee?.phone_number ? (
                <Text w="full" fontSize="sm" fontWeight="normal">
                  Last 4 phone: {editingAttendee.phone_number.slice(-4)}
                </Text>
              ) : null}
              <Text
                w="full"
                fontSize="xs"
                textColor="gray.500"
                fontWeight="normal"
              >
                {editingAttendee?.id}
              </Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign={"left"}>
            <VStack spacing={4} w="full">
              <Stack spacing={0} width="100%">
                {editingAttendee?.transactions?.length ? (
                  <Text w="full" fontWeight="medium">
                    Payments ({editingAttendee?.transactions?.length})
                  </Text>
                ) : null}
                <VStack w="full" gap={2}>
                  {isLoading ? (
                    <VStack w="full" py={4}>
                      <Spinner />
                    </VStack>
                  ) : null}
                  {editingAttendee?.transactions?.map((transaction, index) => (
                    <Stack
                      key={transaction.payment_id}
                      px={2}
                      py={1}
                      rounded={"md"}
                      borderWidth={"1px"}
                      w="full"
                      spacing={0}
                    >
                      <Text fontSize={"xs"}>
                        Date:{" "}
                        {dayjs(transaction.created_at)
                          .tz(timezone)
                          .format("MMM D, YYYY [at] h:mmA")}
                      </Text>
                      <Text fontSize={"xs"}>
                        Payment ID: {transaction.payment_id}
                      </Text>
                      <Text fontSize={"xs"}>
                        Charge Amount: {transaction.display_total}
                      </Text>
                      {transaction.payment_id ? (
                        <HStack pt={2} w="full">
                          <Button
                            w="100%"
                            isDisabled={!!isResending}
                            isLoading={isResending === transaction.payment_id}
                            onClick={() =>
                              resendTickets(transaction.payment_id)
                            }
                          >
                            Send Receipt
                          </Button>
                        </HStack>
                      ) : null}
                    </Stack>
                  ))}
                </VStack>
              </Stack>
              <Stack w="full" spacing={0}>
                <Text fontWeight="medium">
                  Current tickets ({editingAttendee?.tickets?.length || 0})
                </Text>
                <Stack
                  w="full"
                  spacing={2}
                  maxH="320px"
                  overflowY="auto"
                  borderWidth="1px"
                  borderColor="gray.200"
                  rounded="md"
                  p={2}
                  bg="gray.50"
                >
                  {isLoading ? (
                    <VStack w="full" py={4}>
                      <Spinner />
                    </VStack>
                  ) : null}
                  {editingAttendee?.tickets?.map(
                    (t: AlgoliaAttendeeWithTickets["tickets"][0]) => (
                      <Box
                        px={2}
                        py={1}
                        rounded={"md"}
                        borderWidth={"1px"}
                        key={t.id}
                        background="white"
                      >
                        <Text>
                          {ticketTypes.find((tt) => tt.id === t.ticket_type_id)
                            ?.name || "Guestlist"}
                        </Text>
                        {t.purchase_date ? (
                          <Text fontSize="xs">
                            Purchased on:{" "}
                            {dayjs(t.purchase_date)
                              .tz(timezone)
                              .format("MMM D, YYYY [at] h:mmA")}
                          </Text>
                        ) : null}
                        {t.sale ? (
                          <Stack width={"full"} gap={0}>
                            {t.sale.tender_type ? (
                              <Text fontSize="xs" w="full">
                                Payment Method: {t.sale.tender_type}
                              </Text>
                            ) : null}
                            {t.sale.payment_id ? (
                              <Text fontSize="xs" w="full">
                                Payment ID: {t.sale.payment_id}
                              </Text>
                            ) : null}
                            {t.sale.source ? (
                              <Text fontSize="xs" w="full">
                                Location: {t.sale.source}
                              </Text>
                            ) : null}
                            {t.sale.source ? (
                              <Text fontSize="xs" w="full">
                                Price (Incl. Fees): {t.sale.display_total}
                              </Text>
                            ) : null}
                            {t.sale.source ? (
                              <Text fontSize="xs" w="full">
                                Date:{" "}
                                {dayjs(t.sale.created_at)
                                  .tz(timezone)
                                  .format("MMM D, YYYY [at] h:mmA")}
                              </Text>
                            ) : null}
                            {t.sale.purchased_by ? (
                              <Text fontSize="xs" w="full">
                                Purchased By: {t.sale.purchased_by}
                              </Text>
                            ) : null}
                          </Stack>
                        ) : null}
                        <Text color="gray.500" fontSize={"xs"}>
                          {t.id}
                        </Text>
                      </Box>
                    )
                  )}
                </Stack>
              </Stack>
              <Stack w="full" spacing={0}>
                <Text fontWeight="medium">
                  Transferred tickets (
                  {editingAttendee?.transferred_tickets?.length || 0})
                </Text>
                <Stack
                  w="full"
                  spacing={2}
                  maxH="320px"
                  overflowY="auto"
                  borderWidth="1px"
                  borderColor="gray.200"
                  rounded="md"
                  p={2}
                  bg="gray.50"
                  display={
                    editingAttendee?.transferred_tickets?.length > 0
                      ? "flex"
                      : "none"
                  }
                >
                  {editingAttendee?.transferred_tickets?.map((ticket) => {
                    const isWaitlistSold =
                      getTransferredTicketKind(ticket, editingAttendee.id) ===
                      "waitlist_sold";
                    const currentOwner = ticket.current_owner;

                    return (
                      <Box
                        px={2}
                        py={1}
                        rounded="md"
                        borderWidth="1px"
                        key={`${ticket.id}-${ticket.transfer_id || "transfer"}`}
                        background="white"
                      >
                        <HStack justifyContent="space-between">
                          <Text>
                            {ticketTypes.find(
                              (ticketType) =>
                                ticketType.id === ticket.ticket_type_id
                            )?.name || "Unknown ticket type"}
                          </Text>
                          <Badge
                            colorScheme={isWaitlistSold ? "yellow" : "red"}
                          >
                            {isWaitlistSold ? "WAITLIST SOLD" : "TRANSFERRED"}
                          </Badge>
                        </HStack>
                        {ticket.purchased_at ? (
                          <Text fontSize="xs">
                            Purchased on:{" "}
                            {dayjs(ticket.purchased_at)
                              .tz(timezone)
                              .format("MMM D, YYYY [at] h:mmA")}
                          </Text>
                        ) : null}
                        {ticket.payment_id ? (
                          <Text fontSize="xs">
                            Payment ID: {ticket.payment_id}
                          </Text>
                        ) : null}
                        {ticket.transfer_date ? (
                          <Text fontSize="xs">
                            Transfer Date:{" "}
                            {dayjs(ticket.transfer_date)
                              .tz(timezone)
                              .format("MMM D, YYYY [at] h:mmA")}
                          </Text>
                        ) : null}
                        {currentOwner ? (
                          <Box bg="gray.100" p={2} rounded="md" mt={1}>
                            <Text fontSize="xs" fontWeight="bold">
                              Current owner
                            </Text>
                            <Text fontSize="xs">
                              {[currentOwner.first_name, currentOwner.last_name]
                                .filter(Boolean)
                                .join(" ") || "No name"}
                            </Text>
                            {currentOwner.email ? (
                              <Text fontSize="xs">{currentOwner.email}</Text>
                            ) : null}
                            <Text fontSize="xs" color="gray.500">
                              {currentOwner.id}
                            </Text>
                          </Box>
                        ) : (
                          <Text fontSize="xs" color="gray.500">
                            Current owner information unavailable
                          </Text>
                        )}
                        <Text color="gray.500" fontSize="xs">
                          {ticket.id}
                        </Text>
                      </Box>
                    );
                  })}
                </Stack>
              </Stack>
              <VStack spacing={0} width="100%">
                <HStack w="full">
                  <Text fontWeight="medium">Notes</Text>
                </HStack>
                <Textarea
                  noOfLines={3}
                  placeholder="Add here notes about this attendee"
                  disabled={isLoading}
                  {...register("notes")}
                />
                <ErrorMessage
                  name={`notes`}
                  errors={errors}
                  render={({ message }) => (
                    <Text pt={2} fontSize="sm" color="kydRed.200">
                      {message}
                    </Text>
                  )}
                />
              </VStack>
              <VStack
                spacing={0}
                width="100%"
                position="relative"
                ref={bodyRef}
              >
                <HStack w="full">
                  <Text fontWeight="medium">Tags</Text>
                </HStack>

                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <CreatableSelect
                      {...register("tags")}
                      placeholder={"Start writing to create tags"}
                      defaultValue={field.value?.map((v) => ({
                        label: v,
                        value: v,
                      }))}
                      isMulti
                      onChange={(value: { label: string; value: string }[]) => {
                        field.onChange(value.map((t) => t.value));
                      }}
                      chakraStyles={{
                        dropdownIndicator: (provided, state) => ({
                          ...provided,
                          background: "white",
                          w: "20px",
                        }),
                        container: (provided) => ({
                          ...provided,
                          width: "100%",
                          background: "white",
                          zIndex: 100,
                        }),

                        indicatorSeparator: (provided, state) => ({
                          ...provided,
                          display: "white",
                        }),
                      }}
                    />
                  )}
                />
              </VStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading || !!isResending}
                w={"auto"}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                variant="kydDark"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading || !isValid || !!isResending}
                onClick={handleSubmit((data) => _onSave(data))}
              >
                Save
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withEditAttendeeDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={EditAttendeeDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default EditAttendeeDialog;
